"use client";

import { useState } from "react";
import { MdThumbUp, MdThumbDown } from "react-icons/md";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { useAppDispatch } from "@/redux/store";
import { updateFeedBackRating, updateFeedBackForm } from "@/redux/actions";
import Modal from "components/common/Modal";

interface FeedbackProps {
  generationId: number;
  feedbackRating?: Nullable<string>;
  feedbackComment?: Nullable<string>;
  existingFeedbackTooSlow?: Nullable<boolean>;
  existingFeedbackWrongOutput?: Nullable<boolean>;
}

const Feedback: React.FC<FeedbackProps> = ({
  generationId,
  feedbackRating = null,
  feedbackComment = null,
  existingFeedbackTooSlow = null,
  existingFeedbackWrongOutput = null,
}) => {
  const dispatch = useAppDispatch();
  const [showFeedbackComment, setShowFeedbackComment] = useState(false);
  const [text, setText] = useState(feedbackComment || "");
  const [feedbackTooSlow, setFeedbackTooSlow] = useState(
    Boolean(existingFeedbackTooSlow)
  );
  const [feedbackWrongOutput, setFeedbackWringOutput] = useState(
    Boolean(existingFeedbackWrongOutput)
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const { classes, cx } = useStyles({
    feedbackRating,
    feedbackComment,
    feedbackTooSlow,
    feedbackWrongOutput,
  });

  const handleThumbsUp = async () => {
    if (isUpdating) return;
    setIsUpdating(true);

    if (feedbackRating === "upvote") {
      await dispatch(
        updateFeedBackRating({
          feedbackRating: null,
          generationId,
        })
      );
    } else {
      await dispatch(
        updateFeedBackRating({
          feedbackRating: "upvote",
          generationId,
        })
      );
      if (!feedbackRating) {
        setShowFeedbackComment(true);
      }
    }
    setIsUpdating(false);
  };

  const handleThumbsDown = async () => {
    if (isUpdating) return;
    setIsUpdating(true);

    if (feedbackRating === "downvote") {
      await dispatch(
        updateFeedBackRating({
          feedbackRating: null,
          generationId,
        })
      );
    } else {
      await dispatch(
        updateFeedBackRating({
          feedbackRating: "downvote",
          generationId,
        })
      );
      if (!feedbackRating) {
        setShowFeedbackComment(true);
      }
    }
    setIsUpdating(false);
  };

  const handleCloseFeedbackComment = () => {
    setShowFeedbackComment(false);
  };

  const handleSubmitFeedbackComment = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    setShowFeedbackComment(false);

    await dispatch(
      updateFeedBackForm({
        feedbackComment: text,
        feedbackTooSlow,
        feedbackWrongOutput,
        generationId,
      })
    );
    setIsUpdating(false);
  };

  const handleFeedbackTooSlow = () => {
    setFeedbackTooSlow(!feedbackTooSlow);
  };

  const handleFeedbackWrongOutput = () => {
    setFeedbackWringOutput(!feedbackWrongOutput);
  };

  return (
    <>
      <div className={classes.feedbackContainer}>
        <button
          className={cx(classes.iconButton, classes.upvote)}
          aria-label="Feedback Upvote"
          onClick={handleThumbsUp}
          disabled={isUpdating}
        >
          <MdThumbUp />
        </button>
        <button
          className={cx(classes.iconButton, classes.downvote)}
          aria-label="Feedback Downvote"
          onClick={handleThumbsDown}
          disabled={isUpdating}
        >
          <MdThumbDown />
        </button>
      </div>
      <Modal
        heading="Help us improve"
        open={showFeedbackComment}
        onClose={handleCloseFeedbackComment}
        className={classes.modal}
      >
        <div className={classes.modalContent}>
          <p className={classes.modalDescription}>
            What could have been better?
          </p>
          <div className={classes.presetsContainer}>
            <button
              className={cx(classes.presetButton, {
                [classes.presetButtonActive]: feedbackTooSlow,
              })}
              onClick={handleFeedbackTooSlow}
            >
              Took too long
            </button>
            <button
              className={cx(classes.presetButton, {
                [classes.presetButtonActive]: feedbackWrongOutput,
              })}
              onClick={handleFeedbackWrongOutput}
            >
              Not what I wanted
            </button>
          </div>
          <textarea
            className={classes.textArea}
            value={text}
            placeholder="Tell us more (optional)"
            onChange={(e) => setText(e.target.value)}
            rows={4}
          />
          <div className={classes.modalActions}>
            <button
              className={classes.cancelButton}
              onClick={handleCloseFeedbackComment}
            >
              Cancel
            </button>
            <button
              className={classes.submitButton}
              onClick={handleSubmitFeedbackComment}
              disabled={isUpdating}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Feedback;
